import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useSWR from "swr";
import { fetcher } from "../../../services/fetcher";
import {
  OPERATOR_PORTAL,
  buildOperatorPortalQueryString,
  operatorPortalUrl,
} from "../api/operatorPortalEndpoints";
import { DASHBOARD_OPERATOR_SWR_CONFIG } from "../utils/dashboardQueryKeys";
import {
  extractDashboardPayload,
  hasAnyPositive,
  hasChartSeries,
  parseOperatorSummaryCards,
  parsePaymentTrend,
  parsePolicyStatusBreakdown,
  parsePolicyTrend,
  parseTravellerDemographics,
  parseWalletUsage,
} from "../utils/operatorDashboardCharts";

export default function useOperatorDashboardData({ timeFrame, scope, filters }) {
  const operatorProfile = useSelector((state) => state.operatorProfile);
  const [walletUsageView, setWalletUsageView] = useState("D");
  const [paymentTrendView, setPaymentTrendView] = useState("D");

  const dashboardQuery = useMemo(
    () =>
      buildOperatorPortalQueryString({
        timeFrame,
        scope,
        filters,
        walletUsageView,
        paymentTrendView,
        policyTrendView: timeFrame,
      }),
    [timeFrame, scope, filters, walletUsageView, paymentTrendView]
  );

  const dashboardUrl = useMemo(
    () => operatorPortalUrl(OPERATOR_PORTAL.DASHBOARD, dashboardQuery),
    [dashboardQuery]
  );

  const { data: dashboardRaw, isLoading } = useSWR(
    dashboardUrl,
    fetcher,
    DASHBOARD_OPERATOR_SWR_CONFIG
  );

  const dashboard = useMemo(
    () => extractDashboardPayload(dashboardRaw),
    [dashboardRaw]
  );

  const operatorName =
    operatorProfile?.operator_name ||
    operatorProfile?.name ||
    operatorProfile?.poc_name ||
    null;

  const summaryCards = useMemo(
    () => parseOperatorSummaryCards(dashboard?.summary, dashboard?.policy_statistics),
    [dashboard]
  );

  const { trendCategories, trendValues } = useMemo(
    () => parsePolicyTrend(dashboard?.policy_trend),
    [dashboard]
  );

  const { statusLabels, statusValues } = useMemo(
    () => parsePolicyStatusBreakdown(dashboard?.policy_status_breakdown),
    [dashboard]
  );

  const { ageCategories, maleSeries, femaleSeries } = useMemo(
    () => parseTravellerDemographics(dashboard?.traveller_demographics),
    [dashboard]
  );

  const { walletCategories, rechargeSeries, spendSeries } = useMemo(
    () => parseWalletUsage(dashboard?.wallet_usage),
    [dashboard]
  );

  const { paymentCategories, paymentValues } = useMemo(
    () => parsePaymentTrend(dashboard?.payment_trend),
    [dashboard]
  );

  return {
    operatorName,
    summaryCards,
    walletUsageView,
    setWalletUsageView,
    paymentTrendView,
    setPaymentTrendView,
    isLoading,
    timeFrame,
    trendCategories,
    trendValues,
    hasTrendData: hasChartSeries(trendCategories),
    statusLabels,
    statusValues,
    hasPolicyData: hasAnyPositive(statusValues),
    ageCategories,
    maleSeries,
    femaleSeries,
    hasTravellerData:
      hasChartSeries(ageCategories) &&
      (hasAnyPositive(maleSeries) || hasAnyPositive(femaleSeries)),
    walletCategories,
    rechargeSeries,
    spendSeries,
    hasWalletData:
      hasChartSeries(walletCategories) &&
      (hasAnyPositive(rechargeSeries) || hasAnyPositive(spendSeries)),
    paymentCategories,
    paymentValues,
    hasPaymentData: hasChartSeries(paymentCategories),
  };
}
